// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen, Search, Grid, List, Eye,
  TrendingUp, Star, CalendarDays, Tag,
  Loader, RefreshCw, BarChart3, FolderOpen,
  Plus, Trash2
} from 'lucide-react';
import { apiJSON } from '../config/api';
import notificationService from '../utils/notificationService';
import { useConfirm } from './ConfirmDialog';
import './SmartCollections.css';

const SmartCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTheme, setFilterTheme] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionBooks, setCollectionBooks] = useState([]);
  const [showCollectionDetails, setShowCollectionDetails] = useState(false);
  const [stats, setStats] = useState(null);
  const confirm = useConfirm();

  // Charger les collections
  const loadCollections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiJSON('collections');
      setCollections(response.collections || []);
    } catch (error) {
      console.error('Erreur chargement collections:', error);
      notificationService.error('Erreur lors du chargement des collections');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les statistiques
  const loadStats = useCallback(async () => {
    try {
      const response = await apiJSON('collections/stats/overview');
      setStats(response);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  }, []);

  // Construire les collections
  const buildCollections = useCallback(async (forceRebuild = false) => {
    try {
      setBuilding(true);
      await apiJSON('collections/build', {
        method: 'POST',
        body: JSON.stringify({ force_rebuild: forceRebuild })
      });
      
      notificationService.success('Construction des collections lancée');
      
      // Attendre un peu puis recharger
      setTimeout(() => {
        loadCollections();
        loadStats();
      }, 2000);
      
    } catch (error) {
      console.error('Erreur construction collections:', error);
      notificationService.error('Erreur lors de la construction des collections');
    } finally {
      setBuilding(false);
    }
  }, [loadCollections, loadStats]);

  // Charger les livres d'une collection
  const loadCollectionBooks = useCallback(async (collectionId) => {
    try {
      const response = await apiJSON(`collections/${collectionId}/books`);
      setCollectionBooks(response.books || []);
      setSelectedCollection(response);
      setShowCollectionDetails(true);
    } catch (error) {
      console.error('Erreur chargement livres collection:', error);
      notificationService.error('Erreur lors du chargement des livres');
    }
  }, []);

  // Supprimer une collection
  const deleteCollection = useCallback(async (collectionId) => {
    // Dialogue accessible (focus, Échap) plutôt que `window.confirm`, bloquant
    // et hors charte.
    const ok = await confirm({
      title: 'Supprimer la collection ?',
      message: 'Êtes-vous sûr de vouloir supprimer cette collection ? Cette action est irréversible.',
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;

    try {
      await apiJSON(`collections/${collectionId}`, { method: 'DELETE' });
      notificationService.success('Collection supprimée avec succès');
      loadCollections();
      loadStats();
    } catch (error) {
      console.error('Erreur suppression collection:', error);
      notificationService.error('Erreur lors de la suppression');
    }
  }, [confirm, loadCollections, loadStats]);

  // Charger les données au montage
  useEffect(() => {
    loadCollections();
    loadStats();
  }, [loadCollections, loadStats]);

  // Filtrer et trier les collections
  const filteredAndSortedCollections = collections
    .filter(collection => {
      const matchesSearch = collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           collection.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           collection.theme.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTheme = filterTheme === 'all' || collection.theme === filterTheme;
      
      return matchesSearch && matchesTheme;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'size':
          aVal = a.size;
          bVal = b.size;
          break;
        case 'confidence':
          aVal = a.confidence;
          bVal = b.confidence;
          break;
        case 'created_at':
        default:
          aVal = a.created_at;
          bVal = b.created_at;
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  // Thèmes uniques pour le filtre
  const uniqueThemes = [...new Set(collections.map(c => c.theme))].sort();

  // Formater la date
  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Composant de carte de collection
  const CollectionCard = ({ collection }) => (
    <div className="collection-card">
      <div className="collection-header">
        <div className="collection-icon">
          <FolderOpen size={24} />
        </div>
        <div className="collection-actions">
          <button
            onClick={() => loadCollectionBooks(collection.collection_id)}
            className="action-btn primary"
            title="Voir les livres"
            aria-label="Voir les livres"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => deleteCollection(collection.collection_id)}
            className="action-btn danger"
            title="Supprimer"
            aria-label="Supprimer la collection"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="collection-content">
        <h3 className="collection-name">{collection.name}</h3>
        <p className="collection-description">{collection.description}</p>
        
        <div className="collection-meta">
          <div className="meta-item">
            <Tag size={16} />
            <span>{collection.theme}</span>
          </div>
          <div className="meta-item">
            <BookOpen size={16} />
            <span>{collection.size} livre{collection.size > 1 ? 's' : ''}</span>
          </div>
          <div className="meta-item">
            <Star size={16} />
            <span>{Math.round(collection.confidence * 100)}%</span>
          </div>
        </div>

        <div className="collection-keywords">
          {collection.keywords.slice(0, 3).map((keyword, index) => (
            <span key={index} className="keyword-tag">
              {keyword}
            </span>
          ))}
          {collection.keywords.length > 3 && (
            <span className="keyword-more">
              +{collection.keywords.length - 3}
            </span>
          )}
        </div>

        <div className="collection-footer">
          <span className="collection-date">
            Créée le {formatDate(collection.created_at)}
          </span>
        </div>
      </div>
    </div>
  );

  // Composant de liste de collection
  const CollectionListItem = ({ collection }) => (
    <div className="collection-list-item">
      <div className="collection-list-icon">
        <FolderOpen size={20} />
      </div>

      <div className="collection-list-content">
        <div className="collection-list-main">
          <h3 className="collection-list-name">{collection.name}</h3>
          <p className="collection-list-description">{collection.description}</p>
        </div>

        <div className="collection-list-meta">
          <div className="meta-item">
            <Tag size={16} />
            <span>{collection.theme}</span>
          </div>
          <div className="meta-item">
            <BookOpen size={16} />
            <span>{collection.size} livre{collection.size > 1 ? 's' : ''}</span>
          </div>
          <div className="meta-item">
            <Star size={16} />
            <span>{Math.round(collection.confidence * 100)}%</span>
          </div>
          <div className="meta-item">
            <CalendarDays size={16} />
            <span>{formatDate(collection.created_at)}</span>
          </div>
        </div>
      </div>

      <div className="collection-list-actions">
        <button
          onClick={() => loadCollectionBooks(collection.collection_id)}
          className="action-btn primary"
          title="Voir les livres"
            aria-label="Voir les livres"
        >
          <Eye size={16} />
        </button>
        <button
          onClick={() => deleteCollection(collection.collection_id)}
          className="action-btn danger"
          title="Supprimer"
            aria-label="Supprimer la collection"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  // Modal de détails de collection
  const CollectionDetailsModal = () => {
    if (!showCollectionDetails || !selectedCollection) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowCollectionDetails(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{selectedCollection.collection_name}</h2>
            <button
              onClick={() => setShowCollectionDetails(false)}
              className="modal-close"
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            <div className="collection-details">
              <p className="collection-description">
                {selectedCollection.collection_description}
              </p>
              
              <div className="collection-stats">
                <div className="stat-item">
                  <BookOpen size={16} />
                  <span>{selectedCollection.total} livre{selectedCollection.total > 1 ? 's' : ''}</span>
                </div>
                <div className="stat-item">
                  <Tag size={16} />
                  <span>Thème: {selectedCollection.collection_theme}</span>
                </div>
              </div>

              <div className="collection-books">
                <h3>Livres de la collection</h3>
                <div className="books-grid">
                  {collectionBooks.map(book => (
                    <div key={book.book_id} className="book-item">
                      <div className="book-cover">
                        <BookOpen size={24} />
                      </div>
                      <div className="book-info">
                        <h4>{book.title || 'Sans titre'}</h4>
                        {book.author && <p className="book-author">{book.author}</p>}
                        {book.tags && book.tags.length > 0 && (
                          <div className="book-tags">
                            {book.tags.slice(0, 2).map((tag, index) => (
                              <span key={index} className="book-tag">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="smart-collections-loading">
        <Loader size={48} className="animate-spin" />
        <p>Chargement des collections intelligentes...</p>
      </div>
    );
  }

  return (
    <div className="smart-collections">
      {/* Header avec statistiques */}
      <div className="collections-header">
        <div className="header-main">
          <h1>Collections Intelligentes</h1>
          <p>Regroupements automatiques par thèmes et concepts</p>
        </div>

        {stats && (
          <div className="header-stats">
            <div className="stat-card">
              <BarChart3 size={20} />
              <div className="stat-content">
                <span className="stat-value">{stats.total_collections}</span>
                <span className="stat-label">Collections</span>
              </div>
            </div>
            <div className="stat-card">
              <BookOpen size={20} />
              <div className="stat-content">
                <span className="stat-value">{stats.total_books}</span>
                <span className="stat-label">Livres</span>
              </div>
            </div>
            <div className="stat-card">
              <TrendingUp size={20} />
              <div className="stat-content">
                <span className="stat-value">{stats.average_collection_size}</span>
                <span className="stat-label">Moyenne</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contrôles */}
      <div className="collections-controls">
        <div className="controls-left">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Rechercher dans les collections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            value={filterTheme}
            onChange={(e) => setFilterTheme(e.target.value)}
            className="theme-filter"
          >
            <option value="all">Tous les thèmes</option>
            {uniqueThemes.map(theme => (
              <option key={theme} value={theme}>{theme}</option>
            ))}
          </select>
        </div>

        <div className="controls-right">
          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </button>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="created_at">Date de création</option>
            <option value="name">Nom</option>
            <option value="size">Taille</option>
            <option value="confidence">Confiance</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>

          <button
            onClick={() => buildCollections(false)}
            disabled={building}
            className="build-btn"
          >
            {building ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {building ? 'Construction...' : 'Reconstruire'}
          </button>

          <button
            onClick={() => buildCollections(true)}
            disabled={building}
            className="build-btn force"
          >
            <Plus size={16} />
            Nouvelle
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="collections-content">
        {filteredAndSortedCollections.length === 0 ? (
          <div className="empty-state">
            <FolderOpen size={64} />
            <h3>Aucune collection trouvée</h3>
            <p>
              {searchQuery || filterTheme !== 'all' 
                ? "Aucune collection ne correspond à vos critères"
                : "Aucune collection intelligente n'a encore été créée"
              }
            </p>
            {!searchQuery && filterTheme === 'all' && (
              <button
                onClick={() => buildCollections(false)}
                disabled={building}
                className="build-first-btn"
              >
                {building ? 'Construction...' : 'Créer les premières collections'}
              </button>
            )}
          </div>
        ) : (
          <div className={`collections-container view-${viewMode}`}>
            {viewMode === 'grid' ? (
              <div className="collections-grid">
                {filteredAndSortedCollections.map(collection => (
                  <CollectionCard key={collection.collection_id} collection={collection} />
                ))}
              </div>
            ) : (
              <div className="collections-list">
                {filteredAndSortedCollections.map(collection => (
                  <CollectionListItem key={collection.collection_id} collection={collection} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de détails */}
      <CollectionDetailsModal />

      <style>{`
        .smart-collections {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .collections-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          gap: 24px;
        }

        .header-main h1 {
          font-size: 2rem;
          font-weight: 700;
          margin: 0 0 8px 0;
          color: #1f2937;
        }

        .header-main p {
          color: #6b7280;
          margin: 0;
        }

        .header-stats {
          display: flex;
          gap: 16px;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          min-width: 120px;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .collections-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 16px;
        }

        .controls-left {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          color: #6b7280;
        }

        .search-box input {
          padding: 8px 12px 8px 36px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          min-width: 250px;
        }

        .theme-filter {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .controls-right {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .view-controls {
          display: flex;
          gap: 4px;
        }

        .view-btn {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          color: #6b7280;
        }

        .view-btn.active {
          background: #8B5CF6;
          color: white;
          border-color: #8B5CF6;
        }

        .sort-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .sort-order-btn {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-weight: bold;
        }

        .build-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 14px;
        }

        .build-btn.force {
          background: #8B5CF6;
          color: white;
          border-color: #8B5CF6;
        }

        .build-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .collections-content {
          min-height: 400px;
        }

        .empty-state {
          text-align: center;
          padding: 64px 24px;
          color: #6b7280;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: #374151;
        }

        .build-first-btn {
          margin-top: 16px;
          padding: 12px 24px;
          background: #8B5CF6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }

        .collections-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
        }

        .collection-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
        }

        .collection-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .collection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .collection-icon {
          color: #8B5CF6;
        }

        .collection-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          padding: 6px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          color: #6b7280;
        }

        .action-btn.primary {
          color: #8B5CF6;
          border-color: #8B5CF6;
        }

        .action-btn.danger {
          color: #ef4444;
          border-color: #ef4444;
        }

        .collection-content {
          padding: 16px;
        }

        .collection-name {
          margin: 0 0 8px 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
        }

        .collection-description {
          margin: 0 0 16px 0;
          color: #6b7280;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        .collection-meta {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.75rem;
          color: #6b7280;
        }

        .collection-keywords {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-bottom: 12px;
        }

        .keyword-tag {
          padding: 2px 6px;
          background: #f3f4f6;
          border-radius: 4px;
          font-size: 0.75rem;
          color: #374151;
        }

        .keyword-more {
          padding: 2px 6px;
          color: #6b7280;
          font-size: 0.75rem;
        }

        .collection-footer {
          border-top: 1px solid #f3f4f6;
          padding-top: 12px;
        }

        .collection-date {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .collections-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .collection-list-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }

        .collection-list-icon {
          color: #8B5CF6;
        }

        .collection-list-content {
          flex: 1;
        }

        .collection-list-main {
          margin-bottom: 8px;
        }

        .collection-list-name {
          margin: 0 0 4px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .collection-list-description {
          margin: 0;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .collection-list-meta {
          display: flex;
          gap: 16px;
        }

        .collection-list-actions {
          display: flex;
          gap: 8px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          max-height: 80vh;
          overflow: hidden;
          width: 90%;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          max-height: calc(80vh - 80px);
        }

        .collection-details {
          margin-bottom: 24px;
        }

        .collection-stats {
          display: flex;
          gap: 24px;
          margin: 16px 0;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
        }

        .collection-books h3 {
          margin: 0 0 16px 0;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .books-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }

        .book-item {
          display: flex;
          gap: 12px;
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
        }

        .book-cover {
          color: #8B5CF6;
        }

        .book-info h4 {
          margin: 0 0 4px 0;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .book-author {
          margin: 0 0 8px 0;
          font-size: 0.75rem;
          color: #6b7280;
        }

        .book-tags {
          display: flex;
          gap: 4px;
        }

        .book-tag {
          padding: 2px 4px;
          background: #f3f4f6;
          border-radius: 2px;
          font-size: 0.625rem;
          color: #374151;
        }

        .smart-collections-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 64px;
          color: #6b7280;
        }

        .smart-collections-loading p {
          margin-top: 16px;
        }
      `}</style>
    </div>
  );
};

export default SmartCollections;
